/* ==========================================================================
   Hempz — GA4 dataLayer events
   assets/datalayer-events.js
   ==========================================================================
   One delegated listener for every tagged element on the site. Sections opt in
   by adding data attributes; nothing needs adding here when a new section is
   tagged with an existing pattern.

   Events sent:
     view_item_list    product lists entering the viewport
     select_item       product clicked inside a tagged list
     select_promotion  slideshow CTA clicked
     navigation_click  header nav links and category tiles

   Deliberately NOT sent: page_view and all ecommerce events. Those come from
   Shopify's Google & YouTube channel. Sending them here would double count.
   ========================================================================== */

(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  var firedLists = {};

  function push(payload) {
    window.dataLayer.push(payload);
  }

  /*
    GTM's data layer model merges objects recursively rather than replacing
    them, so an items array from a previous push survives underneath a shorter
    one. Pushing ecommerce: null first clears the branch. This is the pattern
    Google documents for GA4 ecommerce events.
  */
  function pushEcommerce(eventName, ecommerce) {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, ecommerce: ecommerce });
  }

  /* ----------------------------------------------------------------------
     Product lists — featured-collections
     Each block emits a JSON payload once. The desk_grid_mobile_carousel
     layout renders two containers sharing one list id, so firedLists guards
     against sending view_item_list twice for the same list.
     ---------------------------------------------------------------------- */

  function getListData(listId) {
    var el = document.querySelector('[data-fc-list-data="' + listId + '"]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function initProductLists() {
    var containers = document.querySelectorAll('[data-fc-list]');
    if (!containers.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(containers, function (el) {
        sendViewItemList(el.getAttribute('data-fc-list'), el);
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          sendViewItemList(entry.target.getAttribute('data-fc-list'), entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.3 }
    );

    Array.prototype.forEach.call(containers, function (el) {
      observer.observe(el);
    });
  }

  function sendViewItemList(listId, container) {
    if (!listId || firedLists[listId]) return;

    var data = getListData(listId);
    if (!data || !data.items || !data.items.length) return;

    firedLists[listId] = true;

    pushEcommerce('view_item_list', {
      item_list_id: data.item_list_id,
      item_list_name: data.item_list_name,
      list_layout: container.classList.contains('ProductList--carousel') ? 'carousel' : 'grid',
      items: data.items,
    });
  }

  function handleProductClick(target) {
    var container = target.closest('[data-fc-list]');
    if (!container) return false;

    var cell = target.closest('[data-fc-index]');
    if (!cell) return false;

    var listId = container.getAttribute('data-fc-list');
    var data = getListData(listId);
    if (!data) return false;

    var index = parseInt(cell.getAttribute('data-fc-index'), 10);
    var item = data.items.filter(function (i) {
      return i.index === index;
    })[0];
    if (!item) return false;

    pushEcommerce('select_item', {
      item_list_id: data.item_list_id,
      item_list_name: data.item_list_name,
      list_layout: container.classList.contains('ProductList--carousel') ? 'carousel' : 'grid',
      items: [item],
    });

    return true;
  }

  /* ----------------------------------------------------------------------
     Promotions — slideshow
     ---------------------------------------------------------------------- */

  function handlePromotionClick(target) {
    var el = target.closest('[data-track="promotion"]');
    if (!el) return false;

    pushEcommerce('select_promotion', {
      promotion_name: el.getAttribute('data-promo-name') || '',
      creative_slot: el.getAttribute('data-promo-slot') || '',
      creative_name: el.getAttribute('data-promo-creative') || '',
      location_id: el.getAttribute('data-promo-location') || '',
      promotion_cta: el.getAttribute('data-promo-cta') || '',
    });

    return true;
  }

  /* ----------------------------------------------------------------------
     Navigation — header links and category tiles
     ---------------------------------------------------------------------- */

  function handleNavigationClick(target) {
    var el = target.closest('[data-track="navigation"]');
    if (!el) return false;

    /*
      The tagged element is either the link itself (header top-level links) or
      a container delegating for links rendered inside a snippet (dropdowns,
      collection tiles). Resolve the actual anchor either way, and ignore
      clicks on a container that did not land on a link.
    */
    var anchor = el.tagName === 'A' ? el : target.closest('a');
    if (!anchor) return false;

    var href = anchor.getAttribute('href') || '';
    var path = href;
    try {
      path = new URL(href, window.location.origin).pathname;
    } catch (e) {
      /* relative or malformed href — fall back to the raw value */
    }

    /* clear any ecommerce branch left by a previous push on this page */
    window.dataLayer.push({ ecommerce: null });

    push({
      event: 'navigation_click',
      link_text: (el.getAttribute('data-nav-text') || anchor.textContent || '').trim().slice(0, 100),
      link_url: path,
      nav_level: el.getAttribute('data-nav-level') || '',
      page_type: el.getAttribute('data-nav-page-type') || '',
    });

    return true;
  }

  /* ----------------------------------------------------------------------
     Single delegated click handler
     Checks most specific first so a promotion inside a product list can never
     fire two events.
     ---------------------------------------------------------------------- */

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (handlePromotionClick(target)) return;
      if (handleNavigationClick(target)) return;
      handleProductClick(target);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductLists);
  } else {
    initProductLists();
  }
})();